import { registerAuditLabels, type StatusMap } from "@vixio/ui";

export type Channel = "email" | "whatsapp" | "sms" | "push";
export const CHANNELS: Record<Channel, string> = { email: "E-mail", whatsapp: "WhatsApp", sms: "SMS", push: "Push" };
export const CHANNEL_OPTIONS = (Object.keys(CHANNELS) as Channel[]).map((value) => ({ value, label: CHANNELS[value] }));

export type ProviderKind = "smtp" | "hub_whatsapp" | "http";
export const PROVIDER_KINDS: Record<ProviderKind, string> = {
  smtp: "Servidor SMTP",
  hub_whatsapp: "Atendimento (DeskcommCRM)",
  http: "Webhook HTTP (SMS, push ou outro)",
};

export const MESSAGE_STATUS: StatusMap = {
  queued: { label: "na fila", tone: "info" },
  sending: { label: "enviando", tone: "info" },
  sent: { label: "enviado", tone: "ok" },
  dead: { label: "com erro", tone: "crit" },
  skipped: { label: "ignorado", tone: "neutral" },
  suppressed: { label: "suprimido", tone: "warn" },
};

export const CATEGORIES: Record<string, string> = { transacional: "Transacional", operacional: "Operacional", marketing: "Marketing" };

export const SUPPRESSION_REASONS: Record<string, string> = {
  opt_out: "Pedido da pessoa",
  bounce: "Endereço recusado",
  complaint: "Reclamação",
  manual: "Manual",
  lgpd: "LGPD",
};

export const EVENT_LABELS: Record<string, string> = {
  queued: "Entrou na fila",
  deduped: "Pedido repetido (mesma chave)",
  skipped: "Ignorado",
  suppressed: "Suprimido",
  deferred: "Adiado (janela de silêncio)",
  claimed: "Reservado pelo worker",
  sent: "Enviado",
  failed: "Falhou, nova tentativa",
  dead: "Foi para a fila de erro",
  requeued: "Reenfileirado pela equipe",
};

export interface MessageView {
  id: string;
  org_id: string;
  channel: Channel;
  template_key: string;
  locale: string;
  person_id: string | null;
  person_name: string | null;
  to_address: string | null;
  var_keys: string[];
  dedupe_key: string | null;
  priority: "normal" | "urgent";
  origin_type: string | null;
  origin_id: string | null;
  origin_event_id: number | null;
  status: keyof typeof MESSAGE_STATUS;
  status_reason: string | null;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  provider_id: string | null;
  provider_name: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  template_version: number | null;
  template_description: string | null;
  category: string | null;
}

export interface MessageEvent {
  id: number;
  message_id: string;
  kind: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface QueueHealth {
  org_id: string;
  queued: number;
  sending: number;
  sent_7d: number;
  dead: number;
  skipped_7d: number;
  suppressed_7d: number;
  oldest_queued: string | null;
}

export interface TemplateView {
  id: string;
  org_id: string;
  key: string;
  channel: Channel;
  locale: string;
  description: string | null;
  category: string;
  consent_purpose: string | null;
  active: boolean;
  current_version_id: string | null;
  version: number | null;
  subject: string | null;
  body: string | null;
  variables: string[] | null;
  published_at: string | null;
  versions: number;
  sent_count: number;
  updated_at: string;
}

export interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  subject: string | null;
  body: string;
  variables: string[];
  created_at: string;
}

export interface Provider {
  id: string;
  org_id: string;
  channel: Channel;
  kind: ProviderKind;
  name: string;
  config: Record<string, unknown>;
  secret_ref: string | null;
  is_default: boolean;
  active: boolean;
  updated_at: string;
}

export interface Suppression {
  id: string;
  org_id: string;
  channel: Channel;
  address: string;
  person_id: string | null;
  reason: string;
  note: string | null;
  lifted_at: string | null;
  created_at: string;
}

export interface Settings {
  org_id: string;
  quiet_start: string | null;
  quiet_end: string | null;
  timezone: string;
  quiet_channels: Channel[];
  max_attempts: number;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "notifications.enqueue": {
      args: { p_org: U; p_channel: string; p_template_key: string; p_person?: U | null; p_to?: string | null; p_vars?: Record<string, unknown>; p_locale?: string; p_dedupe_key?: string | null };
      returns: U;
    };
    "notifications.requeue": { args: { p_message: U }; returns: null };
    "notifications.cancel": { args: { p_message: U; p_reason?: string | null }; returns: null };
    "notifications.set_settings": {
      args: { p_org: U; p_quiet_start: string | null; p_quiet_end: string | null; p_timezone?: string; p_quiet_channels?: string[]; p_max_attempts?: number };
      returns: null;
    };
    "notifications.upsert_provider": {
      args: { p_org: U; p_channel: string; p_kind: string; p_name: string; p_config?: Record<string, unknown>; p_secret_ref?: string | null; p_is_default?: boolean; p_active?: boolean; p_id?: U | null };
      returns: U;
    };
    "notifications.upsert_template": {
      args: { p_org: U; p_key: string; p_channel: string; p_locale?: string; p_description?: string | null; p_category?: string; p_consent_purpose?: string | null; p_active?: boolean };
      returns: U;
    };
    "notifications.publish_template_version": { args: { p_template: U; p_subject: string | null; p_body: string }; returns: U };
    "notifications.install_system_templates": { args: { p_org: U }; returns: number };
    "notifications.add_suppression": {
      args: { p_org: U; p_channel: string; p_address: string; p_reason?: string; p_note?: string | null; p_person?: U | null };
      returns: U;
    };
    "notifications.lift_suppression": { args: { p_suppression: U; p_note?: string | null }; returns: null };
    "notifications.set_preference": {
      args: { p_org: U; p_person: U; p_channel: string; p_enabled: boolean; p_quiet_start?: string | null; p_quiet_end?: string | null };
      returns: null;
    };
    "notifications.opt_out": { args: { p_message: U }; returns: { ok: boolean; channel: Channel; organization: string; address_hint: string } };
  }
}

registerAuditLabels({
  "notifications.enqueued": "Aviso enviado pela equipe",
  "notifications.requeued": "Aviso reenfileirado",
  "notifications.canceled": "Aviso cancelado",
  "notifications.settings_set": "Janela de silêncio alterada",
  "notifications.provider_upserted": "Provedor de aviso salvo",
  "notifications.template_upserted": "Modelo de aviso salvo",
  "notifications.template_published": "Versão de modelo publicada",
  "notifications.suppressed": "Endereço suprimido",
  "notifications.suppression_lifted": "Supressão retirada",
  "notifications.preference_set": "Preferência de aviso alterada",
  "notifications.opt_out": "Descadastro pelo link",
});
